import type { ColDef } from "ag-grid-enterprise";

// Generic, ADDITIVE post-processing hook (86e277pna, Wave 1 / Challenge 3 Phase A; extracted to
// ag_grid_shared by dilton, Wave 0 / Extraction 3, C3 Phase B / Hiram HARD-4 so the upcoming
// Wave-2 editor grid controller can import the SAME function) — mirrors
// applyFilterValueCallbacks on AgGridSsrmComponentController: colDefs travel through the JSON
// `column_defs.to_json` data attribute (Stimulus Values), so a JS function (`cellClassRules`)
// cannot be sent directly from the server. A colDef may instead carry a plain-JSON marker,
// `errorFieldKeys: string[]` — the (server-side, alias-resolved) set of canonical validation-error
// keys that should highlight this column — which this turns into the actual cellClassRules
// function client-side.
//
// No existing colDefs consumer (Admin::Ai::CallLogPageComponent, Elections::VoterStatusGrid,
// etc.) ever sets `errorFieldKeys`/`highlightWhenAnyError`, so this is a no-op passthrough for
// every other grid — zero behavior change for them.
//
// `highlightWhenAnyError: boolean` (browser-QA follow-up, ColumnDefsHelpers#base_errors_column)
// — a second, independent plain-JSON marker alongside `errorFieldKeys`: instead of matching a
// FIXED set of keys, this column tints whenever the row has ANY `error_fields` at all. Used by
// the shared Errors column so a never-mapped (or future) canonical error key still shows an
// unmissable tint somewhere on the row, even without a matching data-column alias.
export function applyErrorFieldCellClassRules(columnDefs: ColDef[]): ColDef[] {
  return columnDefs.map((col) => {
    const rawCol = col as Record<string, unknown>;
    const errorFieldKeys = rawCol.errorFieldKeys as string[] | undefined;
    const highlightWhenAnyError = rawCol.highlightWhenAnyError === true;
    const hasErrorFieldKeys = !!errorFieldKeys && errorFieldKeys.length > 0;
    if (!hasErrorFieldKeys && !highlightWhenAnyError) return col;

    const { errorFieldKeys: _errorFieldKeys, highlightWhenAnyError: _highlightWhenAnyError, ...rest } = rawCol;
    return {
      ...rest,
      cellClassRules: {
        "ag-grid-cell--has-error": (params: { data?: Record<string, unknown> }) => {
          const rowErrorFields = (params.data?.error_fields as string[] | undefined) ?? [];
          if (highlightWhenAnyError) return rowErrorFields.length > 0;
          return (errorFieldKeys ?? []).some((key) => rowErrorFields.includes(key));
        },
        ...((col.cellClassRules as Record<string, unknown>) ?? {}),
      },
    } as ColDef;
  });
}
